/**
 * "Your Treatment Plan", the page the plan-ready message links to.
 *
 * Source: the treatment plan spec's patient-facing section and the message
 * mockup of 17 September, whose single button is "View My Full Treatment Plan
 * with dosing, details, FAQs, and next steps". Drawn in the 23 September
 * system: the red goal card and the plan rows from the dashboard, and the
 * evidence as stat columns like the Clinical Twin screen.
 *
 * The order is the client's own card: the goal first, then what the clinician
 * chose, then what happened to patients like them, then the detail. The goal
 * leads because the spec says it must: "The patient immediately sees their
 * goal front and center."
 *
 * Two things are deliberately absent.
 *
 * Contraindications. The portal's standing rule marks them clinician-facing,
 * and a list of reasons somebody else must not take a drug is not something a
 * patient who has already been prescribed it can act on.
 *
 * A fresh Clinical Twin query. The figures here are the ones frozen onto the
 * regimen when the clinician chose, so what the patient reads is what their
 * clinician saw. The live comparison lives on the Clinical Twin screen.
 */

import { Link } from 'react-router-dom';
import {
  Card,
  CardHead,
  DISPLAY,
  DISPLAY_MID,
  IconTile,
  PageTitle,
  PortalEmpty,
  PortalError,
  PortalLoading,
  TONE,
  formatNumber,
  formatPortalDate,
  usePortalQuery,
} from './shell';
import { PlanIcon } from './Home';
import { PlanActions, UNIT_TEXT, useLogPlanOpened, usePatientPlanPayload, useSectionViewed, type PatientPlanPayload } from './PlanDecision';
import { ArrowLeft, ArrowRight, Bullseye, Scale, Trophy, Users } from './icons';

interface PlanTreatment {
  therapyId: string;
  name: string;
  category: string;
  useOrIndication: string | null;
  route: string | null;
  dose: string | null;
  frequency: string | null;
  protocolReference: string | null;
  patientRationale: string | null;
  sideEffects: string[];
  goalTags: string[];
}

export interface PatientPlan {
  regimenCode: string;
  status: 'ACTIVE' | 'STOPPED';
  startedAt: string | null;
  stoppedAt: string | null;
  clinicianName: string | null;
  goal: {
    primaryGoal: string | null;
    targetValue: number | null;
    targetUnit: string | null;
    label: string | null;
    headline: string | null;
  };
  treatments: PlanTreatment[];
  evidence: {
    cohortSize: number;
    attainmentRate: number | null;
    attainmentDefinition: string | null;
    avgWeightChange: number | null;
    windowMonths: number;
    sufficient: boolean;
  } | null;
  disclaimer: string;
  clinic: { name: string } | null;
}

/** The regimen page, for a patient whose plan was released before plans carried prices. */
function LegacyPlan() {
  const { data, isLoading, error, refetch } = usePortalQuery<PatientPlan>('plan', '/plan');

  if (isLoading) return <PortalLoading />;

  /*
   * A 404 here is the normal state for most patients most of the time, not a
   * failure. Treating it as an error would greet somebody who has not been
   * prescribed anything with a red box.
   */
  const notFound = error instanceof Error && 'status' in error && (error as { status?: number }).status === 404;
  if (notFound) {
    return (
      <>
        <PageTitle title="Your Treatment Plan" />
        <PortalEmpty
          title="No plan yet"
          body="Your clinician has not chosen a treatment plan for you yet. When they do, it appears here and you will get a message."
          action={
            <Link to="/portal" className="text-[13px] font-semibold hover:underline" style={{ color: TONE.forestText }}>
              Back to your dashboard
            </Link>
          }
        />
      </>
    );
  }

  if (error) {
    return (
      <PortalError
        message={error instanceof Error ? error.message : 'Could not load your plan'}
        retry={() => void refetch()}
      />
    );
  }
  if (!data) return null;

  const clinic = data.clinic?.name ?? 'your clinic';
  const evidence = data.evidence;

  return (
    <div className="space-y-[8px] pb-1">
      <PageTitle
        title="Your Treatment Plan"
        subtitle={
          data.status === 'STOPPED'
            ? `Ended ${formatPortalDate(data.stoppedAt)}`
            : data.startedAt
              ? `Started ${formatPortalDate(data.startedAt)}. Personalized for your goals and lab results.`
              : 'Personalized for your goals and lab results.'
        }
      />

      {/* The goal, first and loudest, as it was when the plan was chosen. */}
      {data.goal.headline ? (
        <div
          className="flex min-h-[52px] items-center gap-[15px] rounded-[9px] border px-[14px] py-[9px]"
          style={{ background: 'linear-gradient(90deg, #fde3e3 0%, #fde8e7 100%)', borderColor: '#f6d3d3' }}
        >
          <span className="shrink-0 text-alert">
            <Bullseye size={31} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[10.5px] font-semibold uppercase leading-none tracking-[0.07em] text-alert">
              Your goal
            </span>
            <span
              className="mt-[5px] block truncate font-display text-[25px] font-semibold leading-[1.02] text-alert"
              style={{ ...DISPLAY, letterSpacing: '-0.01em' }}
            >
              {data.goal.headline}
            </span>
          </span>
        </div>
      ) : (
        <div className="rounded-[9px] border border-dashed px-[14px] py-[12px]" style={{ borderColor: '#f0c9c9' }}>
          <p className="text-[13px] leading-snug" style={{ color: TONE.body }}>
            No goal is recorded on this plan. Ask {clinic} to add one so your progress can be measured
            against it.
          </p>
        </div>
      )}

      {data.status === 'STOPPED' && (
        <Card className="px-[13px] py-[12px]" style={{ background: 'var(--paper-deep)' }}>
          <p className="text-[13px] font-semibold" style={{ color: TONE.heading }}>
            This plan has ended
          </p>
          <p className="mt-[3px] text-[12.5px] leading-snug" style={{ color: TONE.body }}>
            Speak to {clinic} about what comes next. Your results stay here either way.
          </p>
        </Card>
      )}

      {/* What the clinician chose, with the dosing the message promises. */}
      <Card className="px-[13px] pb-[10px] pt-[11px]">
        <CardHead label="Your clinician's plan" />
        <ul className="mt-[10px] space-y-[8px]">
          {data.treatments.map((treatment) => (
            <li key={treatment.therapyId} className="rounded-[7px] px-[4px] pb-[10px] pt-[4px]" style={{ background: TONE.row }}>
              <div className="flex items-center gap-[14px]">
                <IconTile size={36}>
                  <PlanIcon category={treatment.category} />
                </IconTile>
                <div className="min-w-0 flex-1 py-[3px] pr-2">
                  <p
                    className="font-display text-[13.5px] font-semibold leading-tight"
                    style={{ color: TONE.heading, fontVariationSettings: '"opsz" 24' }}
                  >
                    {treatment.name}
                  </p>
                  {treatment.useOrIndication && (
                    <p className="mt-[2px] text-[11px] leading-snug" style={{ color: '#383f4c' }}>
                      {treatment.useOrIndication}
                    </p>
                  )}
                </div>
              </div>

              {(treatment.dose || treatment.route || treatment.frequency) && (
                <dl className="ml-[50px] mr-2 mt-[8px] grid grid-cols-2 gap-x-3 gap-y-1 border-t pt-[8px] text-[11.5px]" style={{ borderColor: '#e2e4e1' }}>
                  {treatment.dose && (
                    <div>
                      <dt style={{ color: TONE.faint }}>Dose</dt>
                      <dd className="font-semibold" style={{ color: TONE.heading }}>
                        {treatment.dose}
                      </dd>
                    </div>
                  )}
                  {(treatment.route || treatment.frequency) && (
                    <div>
                      <dt style={{ color: TONE.faint }}>How and how often</dt>
                      <dd className="font-semibold" style={{ color: TONE.heading }}>
                        {[treatment.route, treatment.frequency].filter(Boolean).join(', ')}
                      </dd>
                    </div>
                  )}
                </dl>
              )}

              {treatment.sideEffects.length > 0 && (
                <div className="ml-[50px] mr-2 mt-[8px] border-t pt-[8px]" style={{ borderColor: '#e2e4e1' }}>
                  <p className="text-[11.5px] font-semibold" style={{ color: TONE.heading }}>
                    What some people notice
                  </p>
                  <p className="mt-[2px] text-[11.5px] leading-snug" style={{ color: TONE.body }}>
                    {treatment.sideEffects.join(', ')}. Tell {clinic} if anything is troubling you, or if you
                    are thinking about stopping.
                  </p>
                </div>
              )}

              {treatment.protocolReference && (
                <p className="ml-[50px] mt-[6px] text-[10.5px]" style={{ color: TONE.faint }}>
                  Protocol: {treatment.protocolReference}
                </p>
              )}
            </li>
          ))}
        </ul>
      </Card>

      {/* What happened to patients like them, exactly as the clinician saw it. */}
      <Card className="px-[13px] pb-[12px] pt-[12px]">
        <div className="flex items-start gap-[12px]">
          <span className="mt-[1px] shrink-0 text-forest">
            <Users size={25} strokeWidth={1.6} />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-[16px] font-semibold leading-tight" style={{ ...DISPLAY_MID, color: TONE.heading }}>
              Real Results From Patients Like You
            </h2>
            <p className="mt-[3px] text-[11px] leading-snug" style={{ color: '#4f5b6e' }}>
              The figures your clinician saw when they chose this plan.
            </p>
          </div>
        </div>
        {evidence && evidence.sufficient ? (
          <div className="mt-[12px] grid grid-cols-3">
            <EvidenceStat icon={<Users size={19} strokeWidth={1.6} />} value={formatNumber(evidence.cohortSize)} caption="Similar patients" />
            <EvidenceStat
              icon={<Scale size={19} strokeWidth={1.6} />}
              value={
                evidence.avgWeightChange !== null
                  ? `${evidence.avgWeightChange > 0 ? '+' : ''}${formatNumber(evidence.avgWeightChange, 1)} lbs`
                  : null
              }
              caption={`Average weight change at ${evidence.windowMonths} months`}
              divider
            />
            <EvidenceStat
              icon={<Trophy size={19} strokeWidth={1.6} />}
              value={evidence.attainmentRate !== null ? `${evidence.attainmentRate}%` : null}
              caption={evidence.attainmentDefinition ?? 'Reached their goal'}
              divider
            />
          </div>
        ) : (
          <p className="mt-[10px] text-[12.5px] leading-relaxed" style={{ color: TONE.body }}>
            <span className="font-semibold" style={{ color: TONE.heading }}>
              Limited MCOS data.
            </span>{' '}
            Not enough patients had completed this exact combination for MCOS to report outcomes when your
            plan was chosen, so no figures are shown. Your clinician chose this plan based on your own
            results.
          </p>
        )}
        <p className="mt-[10px] border-t pt-[8px] text-[10.5px] leading-snug" style={{ borderColor: '#ecebe7', color: TONE.faint }}>
          These describe outcomes in other patients and do not guarantee your own results.
        </p>
      </Card>

      <Card className="px-[13px] py-[11px]">
        <CardHead label="Questions" />
        <dl className="mt-[6px] divide-y text-[12.5px]" style={{ borderColor: '#ecebe7' }}>
          {[
            {
              q: 'Who chose this?',
              a: `${data.clinicianName ?? 'Your clinician'}${data.clinic ? ` at ${data.clinic.name}` : ''}, after reviewing your results.`,
            },
            {
              q: 'What if I want to change or stop?',
              a: `Contact ${clinic}. Do not stop on your own without telling them, because your monthly check-in is how they know how you are doing.`,
            },
            {
              q: 'How will we know if it is working?',
              a: 'Your monthly check-in and your next blood draw. Both feed your progress screen.',
            },
            { q: 'Plan reference', a: data.regimenCode },
          ].map((item) => (
            <div key={item.q} className="py-[8px]" style={{ borderColor: '#ecebe7' }}>
              <dt className="font-semibold" style={{ color: TONE.heading }}>
                {item.q}
              </dt>
              <dd className="mt-[2px] leading-snug" style={{ color: TONE.body }}>
                {item.a}
              </dd>
            </div>
          ))}
        </dl>
      </Card>

      <Link
        to="/portal/health"
        className="flex h-[40px] items-center justify-center gap-2 rounded-[8px] bg-forest text-[13px] font-semibold text-white transition-colors hover:bg-forest-deep"
      >
        See how you compare with patients like you
        <ArrowRight size={15} strokeWidth={2} />
      </Link>

      <p className="px-1 pt-1 text-[10.5px] leading-relaxed" style={{ color: TONE.faint }}>
        {data.disclaimer}
      </p>
    </div>
  );
}

function EvidenceStat({
  icon,
  value,
  caption,
  divider = false,
}: {
  icon: React.ReactNode;
  value: string | null;
  caption: string;
  divider?: boolean;
}) {
  return (
    <div className={`flex min-w-0 flex-col items-center px-[5px] text-center ${divider ? 'border-l' : ''}`} style={{ borderColor: '#e6e8e7' }}>
      <IconTile size={32} round>
        {icon}
      </IconTile>
      <p className="mt-[6px] whitespace-nowrap font-display text-[19px] font-semibold leading-none" style={{ ...DISPLAY, color: TONE.heading }}>
        {value ?? '-'}
      </p>
      <p className="mt-[4px] text-[10.5px] leading-[1.3]" style={{ color: '#5b6985' }}>
        {caption}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The plan the clinic sent (boards 03 and 04)
 * ------------------------------------------------------------------ */

const LINE = '#e6e3dc';
const MUTED = '#5b6168';

/** "nausea, reduced appetite" -> "Nausea, reduced appetite". */
function sentence(text: string): string {
  const trimmed = text.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : '';
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[12px] font-semibold uppercase tracking-[0.12em]" style={{ color: '#1c2b3a' }}>
      {children}
    </div>
  );
}

function SentPlan({ payload }: { payload: PatientPlanPayload }) {
  useLogPlanOpened(payload);
  const { plan } = payload;
  const clinic = payload.clinicName ?? 'Your clinic';
  const from = [payload.clinicianName, payload.clinicName].filter(Boolean).join(', ');
  const expect = plan.whatToExpect;
  const itemsRef = useSectionViewed<HTMLDivElement>(plan.planId, 'items');
  const priceRef = useSectionViewed<HTMLDivElement>(plan.planId, 'price');
  const expectRef = useSectionViewed<HTMLDivElement>(plan.planId, 'what_to_expect');
  const twinRef = useSectionViewed<HTMLDivElement>(plan.planId, 'twin');

  return (
    <div className="flex flex-col gap-3 pb-1 pt-2" data-testid="sent-plan">
      <Link to="/portal" className="flex items-center gap-[6px] text-[14px]" style={{ color: TONE.forestText }}>
        <ArrowLeft size={16} />
        Dashboard
      </Link>
      <h1 className="font-display text-[26px] font-semibold leading-tight" style={{ ...DISPLAY, color: TONE.heading }}>
        {plan.title ?? 'Your treatment plan'}
      </h1>

      {/* Board 16: the clinician who prescribed it, on a signed plan. */}
      {plan.prescribedBy && (
        <div className="flex items-center gap-3 rounded-[14px] border bg-white px-[14px] py-[12px]" style={{ borderColor: LINE }} data-testid="prescribed-by">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-[14px] font-semibold" style={{ background: '#e6f0ea', color: '#1b3f35' }}>
            {initialsOf(plan.prescribedBy.replace(/^Prescribed by\s+/, ''))}
          </span>
          <div className="min-w-0">
            <div className="text-[14px] font-semibold" style={{ color: TONE.heading }}>{plan.prescribedBy}</div>
            <div className="text-[13px]" style={{ color: MUTED }}>
              {[payload.clinicName, plan.signedAt ? `signed ${plan.signedAt}` : null].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
      )}

      {plan.providerNote &&
        (plan.prescribedBy ? (
          <div className="rounded-[14px] border bg-white p-[14px]" style={{ borderColor: LINE }} data-testid="clinician-note">
            <div className="text-[12px] font-semibold uppercase tracking-[0.12em]" style={{ color: '#1c2b3a' }}>
              Note from {payload.clinicianName ?? 'your clinician'}
            </div>
            <div className="mt-[6px] whitespace-pre-line text-[14px] leading-normal">{plan.providerNote}</div>
          </div>
        ) : (
          <div className="rounded-[14px] p-[14px]" style={{ background: '#e6f0ea', color: '#1b3f35' }} data-testid="clinician-note">
            {from && <div className="text-[12px] font-semibold">From {from}</div>}
            <div className="mt-[6px] whitespace-pre-line text-[14px] leading-normal">{plan.providerNote}</div>
          </div>
        ))}

      {payload.goalHeadline && (
        <div className="flex items-center gap-3 rounded-[12px] px-[14px] py-3" style={{ background: '#fbe3e1' }} data-testid="plan-goal">
          <span className="shrink-0 text-alert">
            <Bullseye size={31} />
          </span>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-alert">Your goal</div>
            <div className="font-display text-[20px] font-semibold leading-tight text-alert" style={DISPLAY}>
              {payload.goalHeadline}
            </div>
          </div>
        </div>
      )}

      <SectionLabel>What's in your plan</SectionLabel>
      <div ref={itemsRef} className="flex flex-col gap-3">
        {plan.items.map((item) => (
          <div key={item.therapyId || item.name} className="rounded-[14px] border bg-white p-[14px]" style={{ borderColor: LINE }} data-testid="plan-item">
            <div className="flex gap-3">
              <IconTile size={40}>
                <PlanIcon category={item.category} />
              </IconTile>
              <div className="min-w-0 flex-1">
                <div className="flex justify-between gap-2">
                  <div className="font-display text-[18px] font-semibold leading-snug" style={{ color: TONE.heading }}>
                    {item.name}
                  </div>
                  <div className="shrink-0 text-[16px] font-semibold" style={{ color: TONE.heading }}>
                    {item.price}
                    {UNIT_TEXT[item.unit]}
                  </div>
                </div>
                {item.purpose && (
                  <div className="text-[13px]" style={{ color: MUTED }}>
                    {item.purpose}
                  </div>
                )}
              </div>
            </div>
            {(item.dose || item.howToTake) && (
              <div className="mt-[10px] grid grid-cols-2 gap-2 border-t pt-[10px] text-[13px]" style={{ borderColor: '#efede7' }}>
                {item.dose && (
                  <div>
                    <div style={{ color: MUTED }}>Dose</div>
                    <div className="font-semibold">{item.dose}</div>
                  </div>
                )}
                {item.howToTake && (
                  <div>
                    <div style={{ color: MUTED }}>How and how often</div>
                    <div className="font-semibold">{item.howToTake}</div>
                  </div>
                )}
              </div>
            )}
            {item.sideEffects && (
              <div className="mt-[10px] text-[13px] leading-[1.45]">
                <b>What some people notice</b>
                <br />
                {sentence(item.sideEffects)}. Tell your care team if anything is troubling you.
              </div>
            )}
          </div>
        ))}
      </div>

      <div ref={priceRef} className="rounded-[14px] border bg-white p-[14px]" style={{ borderColor: LINE }} data-testid="plan-prices">
        <div className="flex items-baseline justify-between">
          <span className="text-[14px]" style={{ color: MUTED }}>
            Monthly total
          </span>
          <span className="font-display text-[24px] font-semibold" style={{ ...DISPLAY, color: TONE.heading }} data-testid="plan-monthly-total">
            {plan.monthlyTotal}
          </span>
        </div>
        {plan.perVisitNote && (
          <div className="text-right text-[12px]" style={{ color: MUTED }}>
            {plan.perVisitNote}
          </div>
        )}
      </div>
      <p className="text-[13px] leading-normal" style={{ color: MUTED }}>
        {clinic} charges the card they have on file when your prescription is placed. MCOS never charges your card.
      </p>

      <div ref={expectRef} className="rounded-[14px] border bg-white p-[14px]" style={{ borderColor: LINE }} data-testid="plan-what-to-expect">
        <SectionLabel>What to expect</SectionLabel>
        <div
          className={`mt-2 font-display font-semibold leading-[1.35] ${plan.whatToExpect.disclaimer ? 'text-[19px]' : 'text-[17px]'}`}
          style={{ ...DISPLAY, color: TONE.heading }}
        >
          {expect.headline}
        </div>
        {expect.checkpoints.map((line) => (
          <div key={line} className="mt-2 text-[13px] leading-normal">
            {line}
          </div>
        ))}
        {payload.twinStats && (
          <div ref={twinRef} className="mt-3 grid grid-cols-2 gap-2" data-testid="twin-stats">
            <div className="rounded-[10px] p-[10px]" style={{ background: '#f4f3ee' }}>
              <div className="font-display text-[20px] font-semibold" style={DISPLAY}>
                {formatNumber(payload.twinStats.similarPatients)}
              </div>
              <div className="text-[12px]" style={{ color: MUTED }}>
                Similar patients
              </div>
            </div>
            <div className="rounded-[10px] p-[10px]" style={{ background: '#f4f3ee' }}>
              <div className="font-display text-[20px] font-semibold" style={DISPLAY}>
                {payload.twinStats.attainmentPercent}%
              </div>
              <div className="text-[12px]" style={{ color: MUTED }}>
                {payload.twinStats.attainmentLabel}
              </div>
            </div>
          </div>
        )}
        <div className="mt-[10px] flex flex-col gap-[6px] text-[13px]">
          {payload.nextLabDraw && (
            <div className="flex justify-between gap-3">
              <span style={{ color: MUTED }}>Next lab draw</span>
              <b>{payload.nextLabDraw}</b>
            </div>
          )}
          <div className="flex justify-between gap-3">
            <span style={{ color: MUTED }}>Check-ins</span>
            <b>Monthly, by text</b>
          </div>
        </div>
        <div className="mt-[10px] border-t pt-[10px] text-[13px] leading-normal" style={{ borderColor: '#efede7' }}>
          {expect.clinicianNote}
        </div>
        {expect.disclaimer && (
          <div className="mt-2 text-[12px] leading-[1.45]" style={{ color: MUTED }}>
            {expect.disclaimer}
          </div>
        )}
      </div>

      <p className="px-1 text-[12.5px] leading-snug" style={{ color: MUTED }}>
        MCOS is the software your clinic uses. Your clinician chose this plan for you.
      </p>

      <PlanActions payload={payload} />
    </div>
  );
}

/** "Dr. Sarah Lin, DO" -> "SL". */
function initialsOf(name: string): string {
  const words = name.replace(/^dr\.?\s+/i, '').split(',')[0]!.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words.length > 1 ? words[words.length - 1]![0] : '')).toUpperCase();
}

/**
 * The plan page. A plan the clinic sent through the treatment plan screen is
 * drawn as boards 03 and 04 show it. A patient whose regimen was released
 * before plans existed has no sent plan, and sees the older regimen page.
 */
export function PortalPlan() {
  const sent = usePatientPlanPayload();
  if (sent.isLoading) return <PortalLoading />;
  if (sent.data) return <SentPlan payload={sent.data} />;
  return <LegacyPlan />;
}
